import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import StripeConnectOnboarding from "@/components/StripeConnectOnboarding";
import StripeConnectProvider from "@/components/StripeConnectProvider";
import { ConnectAccountManagement } from "@stripe/react-connect-js";
import { toast } from "sonner";
import { CreditCard, RefreshCw, CheckCircle2, AlertCircle, ExternalLink, Landmark } from "lucide-react";
import CancelFeedbackDialog from "@/components/CancelFeedbackDialog";
import PlanPicker from "@/components/billing/PlanPicker";
import BillingAddons from "@/components/billing/BillingAddons";

const CONNECT_LABELS = {
  none: { l: "Non configuré", c: "bg-zinc-700/40 text-zinc-300" },
  pending: { l: "En attente d'activation", c: "bg-amber-500/15 text-amber-400" },
  active: { l: "Actif", c: "bg-emerald-500/15 text-emerald-400" },
};
const SUB_LABELS = {
  none: { l: "Aucun abonnement", c: "bg-zinc-700/40 text-zinc-300" },
  trial: { l: "Essai", c: "bg-[#FFD500]/15 text-[#FFD500]" },
  active: { l: "Actif", c: "bg-emerald-500/15 text-emerald-400" },
  past_due: { l: "Impayé", c: "bg-red-500/15 text-red-400" },
  canceled: { l: "Résilié", c: "bg-zinc-700/40 text-zinc-400" },
};

export default function PaymentsSettings() {
  const [st, setSt] = useState(null);
  const [rev, setRev] = useState(null);
  const [busy, setBusy] = useState("");
  const [onboardOpen, setOnboardOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [offer, setOffer] = useState(null);

  const loadOffer = () => api.get("/billing/offer").then(({ data }) => setOffer(data)).catch(() => {});
  const load = () => { loadOffer(); return api.get("/connect/status").then(({ data }) => setSt(data)).catch(() => {}); };
  useEffect(() => {
    load();
    api.get("/revolut/config").then(({ data }) => setRev(data)).catch(() => {});
    const p = new URLSearchParams(window.location.search);
    if (p.get("connect") === "return") {
      api.post("/connect/refresh").then(({ data }) => { setSt((s) => ({ ...(s || {}), status: data.status })); toast.success("Statut des paiements mis à jour"); }).catch(() => {});
      window.history.replaceState({}, "", window.location.pathname);
    }
    if (p.get("sub") === "success") { toast.success("Abonnement activé — merci !"); setTimeout(load, 1500); window.history.replaceState({}, "", window.location.pathname); }
    if (p.get("sub") === "cancel") { toast("Abonnement annulé"); window.history.replaceState({}, "", window.location.pathname); }
  }, []); // eslint-disable-line

  const onboard = () => setOnboardOpen(true);
  const onOnboardExit = async () => {
    try {
      const { data } = await api.post("/connect/refresh");
      setSt((s) => ({ ...(s || {}), status: data.status }));
      if (data.status === "active") toast.success("Paiements activés — vous pouvez encaisser vos clients !");
      else toast("Activation enregistrée. Vous pourrez la reprendre si des informations manquent.");
    } catch { /* noop */ }
    finally { setOnboardOpen(false); load(); }
  };
  const refresh = async () => {
    setBusy("refresh");
    try { const { data } = await api.post("/connect/refresh"); setSt((s) => ({ ...(s || {}), status: data.status })); toast.success("Statut actualisé"); }
    catch { toast.error("Erreur"); } finally { setBusy(""); }
  };
  if (!st) return <p className="text-zinc-500">Chargement…</p>;
  const cs = CONNECT_LABELS[st.status] || CONNECT_LABELS.none;
  const ss = SUB_LABELS[st.subscription_status] || SUB_LABELS.none;
  const storeUrl = st.store_slug ? `${window.location.origin}/boutique/${st.store_slug}` : "";

  return (
    <div className="max-w-2xl space-y-6" data-testid="payments-settings">
      <div data-testid="connect-card" className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="flex items-center gap-2 font-display text-lg font-bold text-white"><CreditCard className="h-5 w-5 text-[#FF6A00]" />Encaissement des paiements clients</h3>
            <p className="mt-1 text-sm text-zinc-400">Activez votre compte Stripe pour recevoir directement les acomptes et soldes réglés par vos clients. La plateforme prélève une commission de {st.commission_pct || 0}%.</p>
          </div>
          <span data-testid="connect-status" className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${cs.c}`}>{cs.l}</span>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {st.status !== "active" && <Button data-testid="connect-onboard-btn" onClick={onboard} className="rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]"><CreditCard className="mr-1 h-4 w-4" />{st.status === "pending" ? "Reprendre l'activation" : "Activer les paiements"}</Button>}
          {st.account_id && <Button data-testid="connect-refresh-btn" onClick={refresh} disabled={busy === "refresh"} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><RefreshCw className={`mr-1 h-4 w-4 ${busy === "refresh" ? "animate-spin" : ""}`} />Actualiser le statut</Button>}
          {st.account_id && <Button data-testid="connect-manage-btn" onClick={() => setManageOpen(true)} variant="outline" className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800"><Landmark className="mr-1 h-4 w-4" />Coordonnées bancaires</Button>}
        </div>
        {st.status === "active" && <p className="mt-3 flex items-center gap-1.5 text-sm text-emerald-400"><CheckCircle2 className="h-4 w-4" />Vous recevez vos paiements directement sur votre compte Stripe.</p>}
        {st.status === "pending" && <p className="mt-3 flex items-center gap-1.5 text-sm text-amber-400"><AlertCircle className="h-4 w-4" />Finalisez la vérification Stripe pour commencer à encaisser.</p>}
      </div>

      <Dialog open={onboardOpen} onOpenChange={setOnboardOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-2xl" data-testid="onboard-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Activer les paiements</DialogTitle>
            <DialogDescription className="text-zinc-400">Renseignez vos informations directement ici — la vérification est assurée de façon sécurisée par Stripe, sans quitter HalgarOS.</DialogDescription>
          </DialogHeader>
          {onboardOpen && <StripeConnectOnboarding publishableKey={st.publishable_key} onExit={onOnboardExit} />}
        </DialogContent>
      </Dialog>

      <Dialog open={manageOpen} onOpenChange={setManageOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-zinc-800 bg-zinc-950 sm:max-w-2xl" data-testid="manage-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Coordonnées & compte bancaire</DialogTitle>
            <DialogDescription className="text-zinc-400">Modifiez votre IBAN, vos informations et votre calendrier de versements — directement dans HalgarOS, de façon sécurisée via Stripe.</DialogDescription>
          </DialogHeader>
          {manageOpen && (
            <div data-testid="stripe-account-management" className="min-h-[400px]">
              <StripeConnectProvider publishableKey={st.publishable_key}>
                <ConnectAccountManagement />
              </StripeConnectProvider>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {st.status === "active" && st.store_slug && (
        <div data-testid="store-link-card" className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
          <h3 className="font-display text-lg font-bold text-white">Votre boutique en ligne</h3>
          <p className="mt-1 text-sm text-zinc-400">Partagez ce lien : vos clients peuvent acheter vos prestations et régler en ligne. Vous recevez les fonds directement (commission plateforme {st.commission_pct || 0}%).</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <input readOnly data-testid="store-url" value={storeUrl} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-300" />
            <Button data-testid="store-copy" onClick={() => { navigator.clipboard.writeText(storeUrl); toast.success("Lien copié"); }} className="rounded-full bg-[#FF6A00] text-xs font-semibold text-white hover:bg-[#E65F00]">Copier</Button>
            <a href={storeUrl} target="_blank" rel="noopener noreferrer"><Button variant="outline" className="rounded-full border-zinc-700 text-xs text-zinc-200 hover:bg-zinc-800">Ouvrir</Button></a>
          </div>
        </div>
      )}

      <div data-testid="subscription-card" className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-display text-lg font-bold text-white">Mon abonnement HalgarOS</h3>
            <p className="mt-1 text-sm text-zinc-400">Choisissez ou changez de formule à tout moment, au mois ou à l'année (2 mois offerts). Paiement par carte via Revolut.</p>
          </div>
          <span data-testid="sub-status" className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${ss.c}`}>{ss.l}</span>
        </div>
        {offer ? <PlanPicker offer={offer} rev={rev} onDone={load} /> : <p className="mt-4 text-sm text-zinc-500">Chargement des formules…</p>}
        {st.subscription_status === "active" && (
          <div className="mt-5 border-t border-zinc-800 pt-4">
            <button data-testid="cancel-sub-btn" onClick={() => setCancelOpen(true)} className="text-sm text-zinc-500 underline-offset-2 transition-colors hover:text-red-400 hover:underline">Résilier mon abonnement</button>
          </div>
        )}
      </div>

      {offer && <BillingAddons offer={offer} onChanged={loadOffer} />}

      <CancelFeedbackDialog open={cancelOpen} onOpenChange={setCancelOpen} onCanceled={load} />
    </div>
  );
}
